'use client';

/**
 * HeaderActionButton - Renders a single header action button
 */

import React from 'react';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import type { HeaderAction, HeaderActionVariant } from './HeaderActionsContext';

// ============================================================================
// Styles
// ============================================================================

const variantStyles: Record<HeaderActionVariant, string> = {
  default: 'bg-white/[0.06] hover:bg-white/[0.1] text-white/80 hover:text-white border-white/[0.1]',
  primary: 'bg-blue-600 hover:bg-blue-500 text-white border-blue-500',
  success: 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500',
  warning: 'bg-amber-600 hover:bg-amber-500 text-white border-amber-500',
  danger: 'bg-blue-600 hover:bg-blue-500 text-white border-blue-500',
  ghost: 'bg-transparent hover:bg-white/[0.06] text-white/60 hover:text-white border-transparent',
};

const baseStyles = `
  inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium
  rounded-lg border transition-all duration-200
  disabled:opacity-50 disabled:cursor-not-allowed
`;

// ============================================================================
// Component
// ============================================================================

interface HeaderActionButtonProps {
  action: HeaderAction;
}

export function HeaderActionButton({ action }: HeaderActionButtonProps) {
  const {
    id,
    label,
    icon,
    onClick,
    href,
    variant = 'default',
    disabled = false,
    loading = false,
    tooltip,
    render,
    desktopOnly,
    mobileOnly,
  } = action;

  // Custom render function takes precedence
  if (render) {
    return <>{render()}</>;
  }

  // Responsive visibility classes
  const visibilityClass = desktopOnly
    ? 'hidden md:inline-flex'
    : mobileOnly
    ? 'inline-flex md:hidden'
    : 'inline-flex';

  const className = `${baseStyles} ${variantStyles[variant]} ${visibilityClass}`;

  const content = (
    <>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : icon ? (
        <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
      ) : null}
      <span>{label}</span>
    </>
  );

  // Link variant
  if (href && !disabled) {
    return (
      <Link href={href} className={className} title={tooltip}>
        {content}
      </Link>
    );
  }

  // Button variant
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      title={tooltip}
    >
      {content}
    </button>
  );
}

// ============================================================================
// HeaderActionsRenderer - Renders all registered actions
// ============================================================================

interface HeaderActionsRendererProps {
  actions: HeaderAction[];
  className?: string;
}

export function HeaderActionsRenderer({ actions, className = '' }: HeaderActionsRendererProps) {
  if (actions.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {actions.map((action) => (
        <HeaderActionButton key={action.id} action={action} />
      ))}
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default HeaderActionButton;
